/* Dloria storefront behaviour: header, wishlist, product cards, add to bag, carousels,
   hotspots, engraving picker, product page pair toggle and set picker, footer accordions,
   video dialog and scroll reveal. */

const root = document.documentElement;
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const canHover = () => window.matchMedia('(hover: hover)').matches;
const desktop = () => window.matchMedia('(min-width: 900px)').matches;
const strings = () => window.DloriaStrings || {};

/* Horizon scrolls .page-wrapper instead of the window on wide screens. */
const scrollOffset = () => Math.max(window.scrollY, document.querySelector('.page-wrapper')?.scrollTop || 0);
const onAnyScroll = (handler) => document.addEventListener('scroll', handler, { passive: true, capture: true });
const lockScroll = (locked) => root.toggleAttribute('scroll-lock', locked);

/* ---------- Scroll reveal ---------- */

let revealObserver = null;

function initReveal(scope = document) {
  const items = scope.querySelectorAll('[data-dl-reveal]:not(.is-in)');
  if (!root.classList.contains('dl-motion') || !('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-in'));
    return;
  }
  revealObserver ||= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        revealObserver.unobserve(entry.target);
      }
    },
    { threshold: 0.1, rootMargin: '0px 0px -30px 0px' }
  );
  items.forEach((el) => {
    const delay = el.getAttribute('data-dl-reveal');
    if (delay) el.style.setProperty('--dl-delay', `${parseInt(delay, 10) || 0}ms`);
    revealObserver.observe(el);
  });
}

/* ---------- Parallax ---------- */

let parallaxItems = [];
let parallaxTicking = false;

function updateParallax() {
  parallaxTicking = false;
  const vh = window.innerHeight;
  for (const el of parallaxItems) {
    const box = el.parentElement?.getBoundingClientRect();
    if (!box || box.bottom < 0 || box.top > vh) continue;
    const progress = (box.top + box.height / 2 - vh / 2) / vh;
    el.style.transform = `translateY(${(progress * -6).toFixed(2)}%)`;
  }
}

function initParallax() {
  parallaxItems = [...document.querySelectorAll('[data-dl-parallax]')];
  if (!parallaxItems.length || reducedMotion() || !root.classList.contains('dl-motion')) return;
  updateParallax();
}

onAnyScroll(() => {
  if (!parallaxItems.length || parallaxTicking) return;
  parallaxTicking = true;
  requestAnimationFrame(updateParallax);
});

/* ---------- Toast ---------- */

const Toast = {
  timer: null,
  el: null,
  show({ title, image, error = false }) {
    const text = strings();
    if (!this.el) {
      this.el = document.createElement('div');
      this.el.className = 'dl-toast dl-scope';
      this.el.setAttribute('role', 'status');
      this.el.setAttribute('aria-live', 'polite');
      this.el.hidden = true;
      document.body.append(this.el);
    }
    const cartUrl = window.Theme?.routes?.cart_url || '/cart';
    this.el.innerHTML = '';
    if (image) {
      const img = document.createElement('img');
      img.src = image;
      img.alt = '';
      img.width = 56;
      img.height = 56;
      this.el.append(img);
    }
    const copy = document.createElement('div');
    copy.className = 'dl-toast__copy';
    const status = document.createElement('div');
    status.className = 'dl-toast__status';
    status.textContent = error ? text.checkBag || 'Please check your bag' : text.added || 'Added to your bag';
    const name = document.createElement('div');
    name.className = 'dl-toast__name';
    name.textContent = title || '';
    copy.append(status, name);
    const cta = document.createElement('a');
    cta.className = 'dl-toast__cta';
    cta.href = cartUrl;
    cta.textContent = text.checkout || 'Checkout';
    this.el.append(copy, cta);
    this.el.hidden = false;
    this.el.style.animation = 'none';
    void this.el.offsetWidth;
    this.el.style.animation = '';
    clearTimeout(this.timer);
    this.timer = setTimeout(() => (this.el.hidden = true), 3600);
  },
};

/* ---------- Cart ---------- */

/* Adds one or more variants to the bag: [{ id, quantity }]. */
async function addLines(lines) {
  const actions = window.Shopify?.actions;
  if (actions?.updateCart) {
    const result = await actions.updateCart({
      lines: lines.map(({ id, quantity = 1 }) => ({ merchandiseId: String(id), quantity })),
    });
    if (result?.userErrors?.length) throw new Error(result.userErrors[0].message || 'Could not add to bag');
    // Sold-out or short-stock lines come back as warnings while the other lines are added
    if (result?.warnings?.length) throw new Error(result.warnings[0].message || 'Could not add to bag');
    return result;
  }
  const response = await fetch(`${window.Theme?.routes?.cart_add_url || '/cart/add'}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: lines.map(({ id, quantity = 1 }) => ({ id: Number(id), quantity })) }),
  });
  const data = await response.json();
  if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not add to bag');
  window.location.href = window.Theme?.routes?.cart_url || '/cart';
  return data;
}

async function addToCart(variantId, quantity = 1) {
  return addLines([{ id: variantId, quantity }]);
}

function cartDrawerAutoOpens() {
  return Boolean(document.querySelector('cart-drawer-component[auto-open]'));
}

async function addAndConfirm(variantId, { title, image, button }) {
  if (button) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
  }
  try {
    await addToCart(variantId);
    if (!cartDrawerAutoOpens()) Toast.show({ title, image });
  } catch (error) {
    console.error(error);
    Toast.show({ title: error.message, image: '', error: true });
  } finally {
    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

/* Opens Horizon's quick-add dialog for products that need more than one choice (for example ring size). */
async function openQuickAdd({ url, title, productId, optionsCount, host }) {
  if (!document.getElementById('quick-add-dialog') || !window.customElements) {
    window.location.href = url;
    return;
  }
  try {
    await Promise.race([
      customElements.whenDefined('quick-add-component'),
      new Promise((_, reject) => setTimeout(() => reject(new Error('quick add unavailable')), 2500)),
    ]);
    const quickAdd = document.createElement('quick-add-component');
    quickAdd.hidden = true;
    quickAdd.dataset.productTitle = title || '';
    quickAdd.dataset.productId = productId || '';
    quickAdd.dataset.quickAddButton = 'choose';
    quickAdd.dataset.productOptionsCount = String(optionsCount || 2);
    Object.defineProperty(quickAdd, 'productPageUrl', { get: () => url, configurable: true });
    (host || document.body).querySelector(':scope > quick-add-component[hidden]')?.remove();
    (host || document.body).append(quickAdd);
    await quickAdd.handleClick(new Event('click', { cancelable: true }));
    const dialog = document.getElementById('quick-add-dialog');
    dialog?.addEventListener('dialog:close', () => quickAdd.remove(), { once: true });
  } catch (error) {
    window.location.href = url;
  }
}

/* Removing a line re-renders the bag and keyboard focus falls back to the page. Keep it in the open
   drawer: on the item that took the removed line's place, or on the empty bag's button. */
function initCartFocusRescue() {
  let lastRow = -1;
  let inDrawer = false;
  document.addEventListener('focusin', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    inDrawer = Boolean(target?.closest('theme-drawer#cart-drawer'));
    const row = target?.closest('#cart-drawer tbody .cart-items__table-row');
    lastRow = row ? [...row.parentElement.children].indexOf(row) : -1;
  });
  window.addEventListener(
    'shopify:cart:lines-update',
    () => {
      if (!inDrawer) return;
      const started = performance.now();
      // Watch for a few seconds: the focused row leaves after its exit animation, and an emptied bag
      // swaps in new markup (through a view transition, so timers rather than animation frames)
      const check = () => {
        const drawer = document.querySelector('theme-drawer#cart-drawer');
        if (!drawer?.isOpen) return;
        const active = document.activeElement;
        if (!active || active === document.body) {
          const rows = [...drawer.querySelectorAll('tbody .cart-items__table-row')];
          const row = rows[Math.min(Math.max(lastRow, 0), rows.length - 1)];
          const next =
            row?.querySelector('.cart-items__title') ||
            drawer.querySelector('.cart-items__empty-button') ||
            drawer.querySelector('.theme-drawer__close-button');
          next?.focus();
        }
        if (performance.now() - started < 4000) setTimeout(check, 100);
      };
      setTimeout(check, 100);
    },
    { capture: true }
  );
}

/* On desktop the cart drawer overlays the page (see dloria.css) instead of squeezing it; a click on
   the dimmed page closes it, as the backdrop does on phones. */
function initCartScrim() {
  document.addEventListener('click', (event) => {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (event.target === drawer && drawer.isOpen) drawer.close();
  });
}

/* ---------- Wishlist ---------- */

const WISHLIST_KEY = 'dloria:wishlist';

const Wishlist = {
  read() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    } catch {
      return [];
    }
  },
  write(items) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    } catch {}
    document.dispatchEvent(new CustomEvent('dloria:wishlist', { detail: { items } }));
  },
  has(handle) {
    return this.read().some((item) => item.handle === handle);
  },
  toggle(item) {
    const items = this.read();
    const index = items.findIndex((entry) => entry.handle === item.handle);
    if (index >= 0) items.splice(index, 1);
    else items.unshift(item);
    this.write(items.slice(0, 60));
    return index < 0;
  },
  remove(handle) {
    this.write(this.read().filter((entry) => entry.handle !== handle));
  },
};

function syncWishButtons(scope = document) {
  const handles = new Set(Wishlist.read().map((item) => item.handle));
  scope.querySelectorAll('[data-dl-wish]').forEach((button) => {
    button.setAttribute('aria-pressed', String(handles.has(button.dataset.handle)));
  });
  document.querySelectorAll('[data-dl-wish-dot]').forEach((dot) => (dot.hidden = handles.size === 0));
}

document.addEventListener('dloria:wishlist', () => syncWishButtons());
window.addEventListener('storage', (event) => {
  if (event.key === WISHLIST_KEY) syncWishButtons();
});

/* ---------- Header ---------- */

class DlHeader extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.items = [...this.querySelectorAll('.dl-nav__item')];
    this.onScroll = () => this.toggleAttribute('data-scrolled', scrollOffset() > 60);
    document.addEventListener('scroll', this.onScroll, { passive: true, capture: true, signal });
    this.onScroll();

    for (const item of this.items) {
      /* Hover intent: a short pause before opening keeps menus from flashing as the pointer crosses the row. */
      item.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType !== 'mouse') return;
          this.warm(item);
          this.schedule(() => this.open(item), this.hasAttribute('data-mega-open') ? 120 : 70);
        },
        { signal }
      );
      item.addEventListener(
        'pointerleave',
        (event) => {
          if (event.pointerType === 'mouse') clearTimeout(this.timer);
        },
        { signal }
      );
      const toggle = item.querySelector(':scope > .dl-nav__toggle');
      toggle?.addEventListener(
        'click',
        () => {
          if (item.hasAttribute('data-open')) this.close();
          else {
            this.warm(item);
            this.open(item);
          }
        },
        { signal }
      );
      const link = item.querySelector(':scope > .dl-nav__link');
      if (!link || !item.hasAttribute('data-has-mega')) continue;
      /* Touch screens can't hover: the first tap opens the menu, a second tap follows the link. */
      link.addEventListener(
        'pointerdown',
        (event) => {
          this.tapTarget = event.pointerType !== 'mouse' && !item.hasAttribute('data-open') ? item : null;
        },
        { signal }
      );
      link.addEventListener(
        'click',
        (event) => {
          if (this.tapTarget !== item || event.detail === 0 || !desktop()) return;
          this.tapTarget = null;
          event.preventDefault();
          this.warm(item);
          this.open(item);
        },
        { signal }
      );
    }

    this.addEventListener(
      'pointerenter',
      (event) => {
        if (event.pointerType === 'mouse') clearTimeout(this.closeTimer);
      },
      { signal }
    );
    this.addEventListener(
      'pointerleave',
      (event) => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(this.timer);
        this.closeTimer = setTimeout(() => this.close(), 160);
      },
      { signal }
    );
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' || !this.hasAttribute('data-mega-open')) return;
        const item = this.querySelector('.dl-nav__item[data-open]');
        const focusInside = item?.contains(document.activeElement);
        this.close();
        if (focusInside) (item.querySelector(':scope > .dl-nav__toggle') || item.querySelector(':scope > .dl-nav__link'))?.focus();
      },
      { signal }
    );
    this.addEventListener(
      'focusout',
      (event) => {
        if (!this.contains(event.relatedTarget)) this.close();
      },
      { signal }
    );
    this.addEventListener(
      'focusin',
      (event) => {
        const item = this.querySelector('.dl-nav__item[data-open]');
        if (item && !item.contains(event.target)) this.close();
      },
      { signal }
    );

    /* Theme editor: show the mega menu that holds the selected "Mega menu image" block. */
    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const item = event.target.closest?.('.dl-nav__item');
        if (item && this.contains(item)) this.open(item);
      },
      { signal }
    );
    document.addEventListener(
      'shopify:block:deselect',
      (event) => {
        if (this.contains(event.target)) this.close();
      },
      { signal }
    );

    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.publishHeights());
    this.resizeObserver.observe(this);
    this.publishHeights();

    this.querySelectorAll('[data-dl-open]').forEach((button) =>
      button.addEventListener('click', () => this.openDialog(button.dataset.dlOpen, button), { signal })
    );
  }

  disconnectedCallback() {
    this.abort?.abort();
    clearTimeout(this.timer);
    clearTimeout(this.closeTimer);
    if (this.onOutside) document.removeEventListener('pointerdown', this.onOutside, true);
    this.resizeObserver?.disconnect();
  }

  schedule(callback, delay) {
    clearTimeout(this.timer);
    this.timer = setTimeout(callback, delay);
  }

  /* Starts loading a mega menu's pictures as soon as the pointer or focus heads for it. */
  warm(item) {
    item.querySelectorAll('.dl-mega img[loading="lazy"]').forEach((image) => (image.loading = 'eager'));
  }

  open(item) {
    clearTimeout(this.timer);
    clearTimeout(this.closeTimer);
    const hasMega = item.hasAttribute('data-has-mega') && desktop();
    this.toggleAttribute('data-mega-switch', hasMega && this.hasAttribute('data-mega-open') && !item.hasAttribute('data-open'));
    this.items.forEach((entry) => {
      const isOpen = entry === item;
      entry.toggleAttribute('data-open', isOpen);
      entry.querySelector(':scope > .dl-nav__toggle')?.setAttribute('aria-expanded', String(isOpen && hasMega));
    });
    this.toggleAttribute('data-mega-open', hasMega);
    this.onOutside ||= (event) => {
      if (!this.contains(event.target)) this.close();
    };
    document.addEventListener('pointerdown', this.onOutside, true);
  }

  close() {
    clearTimeout(this.timer);
    clearTimeout(this.closeTimer);
    this.items.forEach((entry) => {
      entry.removeAttribute('data-open');
      entry.querySelector(':scope > .dl-nav__toggle')?.setAttribute('aria-expanded', 'false');
    });
    this.removeAttribute('data-mega-open');
    this.removeAttribute('data-mega-switch');
    if (this.onOutside) document.removeEventListener('pointerdown', this.onOutside, true);
  }

  openDialog(id, opener) {
    const dialog = document.getElementById(id);
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (dialog.dataset.dlWishlist !== undefined) renderWishlist(dialog);
    dialog.showModal();
    lockScroll(true);
    opener?.setAttribute('aria-expanded', 'true');
    dialog.addEventListener(
      'close',
      () => {
        if (!document.querySelector('dialog.dl-drawer[open]')) lockScroll(false);
        opener?.setAttribute('aria-expanded', 'false');
        if (!document.querySelector('dialog[open]')) opener?.focus();
      },
      { once: true }
    );
  }

  publishHeights() {
    const height = Math.round(this.getBoundingClientRect().height);
    document.body.style.setProperty('--header-height', `${height}px`);
    const group = document.getElementById('header-group');
    let total = 0;
    if (group) {
      for (const child of group.children) if (child instanceof HTMLElement) total += child.offsetHeight;
    }
    document.body.style.setProperty('--header-group-height', `${total || height}px`);
  }
}

function renderWishlist(dialog) {
  const list = dialog.querySelector('[data-dl-wishlist-list]');
  const empty = dialog.querySelector('[data-dl-wishlist-empty]');
  const template = dialog.querySelector('template');
  if (!list || !template) return;
  const items = Wishlist.read();
  list.innerHTML = '';
  empty.hidden = items.length > 0;
  for (const item of items) {
    const row = template.content.firstElementChild.cloneNode(true);
    row.querySelectorAll('a').forEach((a) => (a.href = item.url));
    const img = row.querySelector('img');
    if (item.image) {
      img.src = item.image;
      img.alt = item.title;
    } else img.remove();
    row.querySelector('[data-name]').textContent = item.title;
    row.querySelector('[data-price]').textContent = item.price || '';
    row.querySelector('[data-remove]').addEventListener('click', () => {
      Wishlist.remove(item.handle);
      renderWishlist(dialog);
    });
    list.append(row);
  }
}

document.addEventListener('click', (event) => {
  const openWishlist = event.target.closest('[data-dl-open-wishlist]');
  if (openWishlist) {
    event.preventDefault();
    openWishlist.closest('dialog')?.close();
    document.querySelector('dl-header')?.openDialog('DlWishlist');
    return;
  }
  const close = event.target.closest('[data-dl-close]');
  if (close) close.closest('dialog')?.close();
});

/* Buttons that open a drawer rendered elsewhere on the page, named by aria-controls: the size guide
   button on a ring size option opens the size guide block's drawer. */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-dl-dialog]');
  if (!trigger) return;
  const id = trigger.getAttribute('aria-controls');
  const header = document.querySelector('dl-header');
  if (header) header.openDialog(id, trigger);
  else document.getElementById(id)?.showModal?.();
});

/* Theme editor: show the size guide drawer while its block is selected. */
document.addEventListener('shopify:block:select', (event) => {
  const drawer = event.target;
  if (drawer instanceof HTMLDialogElement && drawer.classList.contains('dl-size-drawer') && !drawer.open) drawer.showModal();
});
document.addEventListener('shopify:block:deselect', (event) => {
  const drawer = event.target;
  if (drawer instanceof HTMLDialogElement && drawer.classList.contains('dl-size-drawer')) drawer.close();
});

document.addEventListener('click', (event) => {
  const dialog = event.target;
  if (dialog instanceof HTMLDialogElement && dialog.classList.contains('dl-drawer') && event.target === dialog) {
    const box = dialog.getBoundingClientRect();
    const inside = event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
    if (!inside) dialog.close();
  }
});

/* ---------- Mobile menu (drill-down panels) ---------- */

class DlMenuDrawer extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.dialog = this.closest('dialog');
    this.root = this.querySelector('[data-dl-panel="root"]');
    this.addEventListener(
      'click',
      (event) => {
        const opener = event.target.closest('[data-dl-panel-open]');
        if (opener) return this.show(opener);
        if (event.target.closest('[data-dl-panel-back]')) return this.back();
        if (event.target.closest('[data-dl-menu-search]')) this.search();
      },
      { signal }
    );
    this.dialog?.addEventListener('close', () => this.reset(), { signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  show(opener) {
    const panel = document.getElementById(opener.getAttribute('aria-controls'));
    if (!panel || !this.root) return;
    this.opener = opener;
    this.root.hidden = true;
    panel.hidden = false;
    opener.setAttribute('aria-expanded', 'true');
    this.dialog?.scrollTo({ top: 0 });
    panel.querySelector('[data-dl-panel-back]')?.focus({ preventScroll: true });
  }

  back() {
    if (!this.root) return;
    this.querySelectorAll('[data-dl-panel]:not([data-dl-panel="root"])').forEach((panel) => (panel.hidden = true));
    this.root.dataset.dir = 'back';
    this.root.hidden = false;
    this.opener?.setAttribute('aria-expanded', 'false');
    this.opener?.focus({ preventScroll: true });
    this.opener?.scrollIntoView({ block: 'nearest' });
  }

  reset() {
    this.querySelectorAll('[data-dl-panel]:not([data-dl-panel="root"])').forEach((panel) => (panel.hidden = true));
    this.querySelectorAll('[data-dl-panel-open]').forEach((opener) => opener.setAttribute('aria-expanded', 'false'));
    if (this.root) {
      this.root.hidden = false;
      delete this.root.dataset.dir;
    }
  }

  search() {
    const modal = document.getElementById('search-modal');
    this.dialog?.close();
    if (modal && typeof modal.showDialog === 'function') modal.showDialog();
    else window.location.href = `${window.Shopify?.routes?.root || '/'}search`;
  }
}

/* ---------- Utility bar message rotator ---------- */

class DlRotator extends HTMLElement {
  connectedCallback() {
    this.messages = [...this.querySelectorAll('[data-dl-message]')];
    if (this.messages.length < 2) return;
    this.index = 0;
    const seconds = parseFloat(this.dataset.interval || '4.5');
    this.timer = setInterval(() => this.next(), Math.max(seconds, 2) * 1000);
    this.addEventListener('pointerenter', () => (this.paused = true));
    this.addEventListener('pointerleave', () => (this.paused = false));
    this.onBlockSelect = (event) => {
      const index = this.messages.findIndex((message) => message.dataset.blockId === event.detail?.blockId);
      if (index < 0) return;
      this.messages.forEach((message, i) => (message.hidden = i !== index));
      this.index = index;
      this.paused = true;
    };
    this.onBlockDeselect = () => (this.paused = false);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  next() {
    if (this.paused) return;
    this.messages[this.index].hidden = true;
    this.index = (this.index + 1) % this.messages.length;
    const current = this.messages[this.index];
    current.hidden = false;
    if (!reducedMotion()) {
      current.classList.remove('is-entering');
      void current.offsetWidth;
      current.classList.add('is-entering');
    }
  }
}

/* ---------- Product card ---------- */

class DlProductCard extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    try {
      this.data = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '{}');
    } catch {
      this.data = {};
    }
    this.selected = this.data.selected || null;
    this.mainImage = this.querySelector('.dl-card__img--main');

    this.querySelectorAll('[data-dl-swatch]').forEach((swatch) =>
      swatch.addEventListener('click', () => this.pick(swatch))
    );
    this.querySelectorAll('[data-dl-add]').forEach((button) =>
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.add(button);
      })
    );
    this.querySelector('[data-dl-wish]')?.addEventListener('click', (event) => {
      event.preventDefault();
      const added = Wishlist.toggle(this.wishItem());
      event.currentTarget.setAttribute('aria-pressed', String(added));
    });
  }

  wishItem() {
    const variant = this.currentVariant();
    return {
      handle: this.data.handle,
      title: this.data.title,
      url: this.data.url,
      image: (variant && variant.image) || this.data.image || '',
      price: (variant && variant.price) || this.data.price || '',
    };
  }

  currentVariant() {
    const variants = this.data.variants || [];
    if (!variants.length) return null;
    if (this.data.colorIndex == null || !this.selected) {
      return variants.find((variant) => variant.available) || variants[0];
    }
    const matches = variants.filter((variant) => variant.options[this.data.colorIndex] === this.selected);
    return matches.find((variant) => variant.available) || matches[0] || null;
  }

  pick(swatch) {
    this.selected = swatch.dataset.value;
    this.querySelectorAll('[data-dl-swatch]').forEach((entry) =>
      entry.setAttribute('aria-pressed', String(entry === swatch))
    );
    const label = this.querySelector('[data-dl-swatch-label]');
    if (label) label.textContent = swatch.dataset.label || swatch.dataset.value;
    const variant = this.currentVariant();
    if (variant?.image && this.mainImage) {
      this.mainImage.srcset = '';
      this.mainImage.src = variant.image;
    }
    if (variant) {
      const link = new URL(this.data.url, window.location.origin);
      link.searchParams.set('variant', variant.id);
      this.querySelectorAll('a[data-dl-product-link]').forEach((a) => (a.href = link.pathname + link.search));
      const price = this.querySelector('[data-dl-price]');
      if (price && variant.price) price.textContent = variant.price;
      const addPrice = this.querySelector('[data-dl-add-price]');
      if (addPrice && variant.price) addPrice.textContent = variant.price;
    }
  }

  add(button) {
    const variant = this.currentVariant();
    if (!variant || this.data.needsChoice) {
      const url = new URL(this.data.url, window.location.origin);
      if (variant) url.searchParams.set('variant', variant.id);
      button.setAttribute('aria-busy', 'true');
      openQuickAdd({
        url: url.pathname + url.search,
        title: this.data.title,
        productId: this.data.id,
        optionsCount: this.data.optionsCount,
        host: this,
      }).finally(() => button.removeAttribute('aria-busy'));
      return;
    }
    if (!variant.available) {
      Toast.show({ title: strings().soldOut || 'Sold out', image: variant.image || this.data.image, error: true });
      return;
    }
    addAndConfirm(variant.id, { title: this.data.title, image: variant.image || this.data.image, button });
  }
}

/* ---------- Carousel arrows ---------- */

class DlRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-dl-track]');
    this.querySelectorAll('[data-dl-step]').forEach((button) =>
      button.addEventListener('click', () => {
        if (!this.track) return;
        const direction = Number(button.dataset.dlStep);
        this.track.scrollBy({
          left: direction * Math.min(this.track.clientWidth * 0.8, 760),
          behavior: reducedMotion() ? 'auto' : 'smooth',
        });
      })
    );
  }
}

/* ---------- Hero hotspots ---------- */

class DlHotspot extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', () => {
      if (this.openedByHover) {
        this.openedByHover = false;
        return;
      }
      this.toggle();
    });
    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse' || !canHover() || this.hasAttribute('data-open')) return;
      this.show();
      this.openedByHover = true;
    });
    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse' && canHover()) this.hide();
    });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.hide();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.hide();
        this.button?.focus();
      }
    });
  }

  toggle() {
    this.hasAttribute('data-open') ? this.hide() : this.show();
  }

  show() {
    this.closest('section')
      ?.querySelectorAll('dl-hotspot[data-open]')
      .forEach((spot) => spot !== this && spot.hide());
    this.setAttribute('data-open', '');
    this.button?.setAttribute('aria-expanded', 'true');
    this.place();
    this.onOutside ||= (event) => {
      if (!this.contains(event.target)) this.hide();
    };
    document.addEventListener('pointerdown', this.onOutside, true);
  }

  /* Keeps the product card inside its image: shifts it sideways and flips it below the dot near the top edge. */
  place() {
    const card = this.querySelector('.dl-hotspot__card');
    if (!card) return;
    card.style.setProperty('--dl-shift', '0px');
    this.removeAttribute('data-flip');
    const frame = (this.closest('.dl-hero__panel') || root).getBoundingClientRect();
    const margin = 12;
    let box = card.getBoundingClientRect();
    if (box.top < Math.max(frame.top, 0) + margin) {
      this.setAttribute('data-flip', '');
      box = card.getBoundingClientRect();
    }
    const minLeft = Math.max(frame.left, 0) + margin;
    const maxRight = Math.min(frame.right, window.innerWidth) - margin;
    let shift = 0;
    if (box.right > maxRight) shift = maxRight - box.right;
    if (box.left + shift < minLeft) shift = minLeft - box.left;
    card.style.setProperty('--dl-shift', `${Math.round(shift)}px`);
  }

  hide() {
    this.openedByHover = false;
    this.removeAttribute('data-open');
    this.button?.setAttribute('aria-expanded', 'false');
    if (this.onOutside) document.removeEventListener('pointerdown', this.onOutside, true);
  }

  disconnectedCallback() {
    if (this.onOutside) document.removeEventListener('pointerdown', this.onOutside, true);
  }
}

/* ---------- Engraving picker ---------- */

class DlEngraver extends HTMLElement {
  connectedCallback() {
    this.options = [...this.querySelectorAll('[data-dl-engraving]')];
    this.image = this.querySelector('.dl-engrave__img');
    this.chip = this.querySelector('[data-dl-engrave-chip]');
    this.price = this.querySelector('[data-dl-engrave-price]');
    this.addPrice = this.querySelector('[data-dl-engrave-add-price]');
    this.details = this.querySelector('[data-dl-engrave-details]');
    this.addButton = this.querySelector('[data-dl-engrave-add]');
    this.finish = this.querySelector('[data-dl-finish]');
    this.options.forEach((option, index) => {
      option.addEventListener('click', () => this.select(index));
      option.addEventListener('keydown', (event) => this.onKey(event, index));
    });
    this.addButton?.addEventListener('click', () => this.add());
    this.finish?.addEventListener('click', (event) => {
      const swatch = event.target.closest('[data-dl-swatch]');
      if (!swatch) return;
      this.metal = swatch.dataset.value;
      this.userPicked = true;
      this.finish.querySelectorAll('[data-dl-swatch]').forEach((entry) => entry.setAttribute('aria-pressed', String(entry === swatch)));
      const label = this.finish.querySelector('[data-dl-swatch-label]');
      if (label) label.textContent = swatch.dataset.label || swatch.dataset.value;
      this.refreshImage(true);
    });
    this.current = Math.max(0, this.options.findIndex((option) => option.getAttribute('aria-selected') === 'true'));
    this.metal = this.startingFinish();
    this.renderFinish();
    document.addEventListener('shopify:block:select', (event) => {
      const index = this.options.findIndex((option) => option.dataset.blockId === event.detail?.blockId);
      if (index >= 0) this.select(index);
    });
  }

  data(index = this.current) {
    try {
      return JSON.parse(this.options[index]?.querySelector('script[type="application/json"]')?.textContent || '{}');
    } catch {
      return {};
    }
  }

  onKey(event, index) {
    const keys = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const next = (index + keys[event.key] + this.options.length) % this.options.length;
    this.select(next);
    this.options[next].focus();
  }

  startingFinish(data = this.data()) {
    const values = (data.colors || []).map((value) => value.value);
    if (data.defaultFinish && values.includes(data.defaultFinish)) return data.defaultFinish;
    return data.selected || values[0] || null;
  }

  select(index) {
    if (index === this.current && this.ready) return;
    this.ready = true;
    this.current = index;
    this.userPicked = false;
    this.metal = this.startingFinish();
    this.options.forEach((option, i) => {
      option.setAttribute('aria-selected', String(i === index));
      option.tabIndex = i === index ? 0 : -1;
    });
    const data = this.data();
    if (this.chip) this.chip.textContent = data.label || '';
    if (this.price && data.price) this.price.textContent = data.price;
    if (this.addPrice && data.price) this.addPrice.textContent = data.price;
    if (this.details && data.url) this.details.href = data.url;
    this.renderFinish();
    this.refreshImage(true);
    if (!reducedMotion()) {
      const letters = this.options[index].querySelectorAll('.dl-engrave__letter');
      letters.forEach((letter, i) => {
        letter.classList.remove('is-entering');
        letter.style.setProperty('--i', `${i * 22}ms`);
        void letter.offsetWidth;
        letter.classList.add('is-entering');
      });
    }
  }

  renderFinish() {
    if (!this.finish) return;
    const data = this.data();
    const values = data.colors || [];
    this.finish.hidden = values.length < 2;
    if (values.length < 2) return;
    if (!values.some((value) => value.value === this.metal)) this.metal = data.selected || values[0].value;
    const container = this.finish.querySelector('[data-dl-swatch-list]');
    container.innerHTML = '';
    for (const value of values) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dl-swatch';
      button.dataset.dlSwatch = '';
      button.dataset.value = value.value;
      button.dataset.label = value.value;
      button.setAttribute('aria-label', value.value);
      button.title = value.value;
      button.setAttribute('aria-pressed', String(value.value === this.metal));
      button.innerHTML = `<span class="dl-swatch__ring"><span class="dl-swatch__fill" style="--swatch:${value.swatch}"></span></span>`;
      container.append(button);
    }
    const label = this.finish.querySelector('[data-dl-swatch-label]');
    if (label) label.textContent = this.metal;
  }

  variant() {
    const data = this.data();
    const variants = data.variants || [];
    if (data.colorIndex == null || !this.metal) return variants.find((variant) => variant.available) || variants[0];
    const matches = variants.filter((variant) => variant.options[data.colorIndex] === this.metal);
    return matches.find((variant) => variant.available) || matches[0];
  }

  refreshImage(animate = false) {
    const data = this.data();
    const variant = this.variant();
    let src = data.customImage;
    if ((this.userPicked || data.useVariantImage || !src) && variant?.image) src = variant.image;
    src ||= data.customImage || data.image;
    if (!this.image || !src) return;
    this.image.srcset = '';
    this.image.src = src;
    this.image.alt = data.alt || '';
    if (animate && !reducedMotion()) {
      this.image.classList.remove('is-entering');
      void this.image.offsetWidth;
      this.image.classList.add('is-entering');
    }
  }

  add() {
    const data = this.data();
    const variant = this.variant();
    if (!variant) return;
    if (data.needsChoice) {
      openQuickAdd({ url: `${data.url}?variant=${variant.id}`, title: data.title, productId: data.id, optionsCount: data.optionsCount, host: this });
      return;
    }
    if (!variant.available) {
      Toast.show({ title: strings().soldOut || 'Sold out', image: variant.image, error: true });
      return;
    }
    addAndConfirm(variant.id, { title: data.cartTitle || data.title, image: variant.image || data.image, button: this.addButton });
  }
}

/* ---------- Footer accordions ---------- */

document.addEventListener('click', (event) => {
  const title = event.target.closest('[data-dl-footer-toggle]');
  if (!title || desktop()) return;
  const column = title.closest('.dl-footer__col');
  const open = column.toggleAttribute('data-open');
  title.setAttribute('aria-expanded', String(open));
  const sign = title.querySelector('.dl-footer__col-sign');
  if (sign) sign.textContent = open ? '−' : '+';
});

/* ---------- Video dialog ---------- */

function embedUrl(url) {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('youtu')) {
      const id = parsed.hostname.includes('youtu.be') ? parsed.pathname.slice(1) : parsed.searchParams.get('v') || parsed.pathname.split('/').pop();
      return `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`;
    }
    if (parsed.hostname.includes('vimeo')) {
      return `https://player.vimeo.com/video/${parsed.pathname.split('/').pop()}?autoplay=1`;
    }
  } catch {}
  return null;
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-dl-video]');
  if (!trigger) return;
  const src = embedUrl(trigger.dataset.dlVideo);
  if (!src) return;
  event.preventDefault();
  let dialog = document.getElementById('DlVideoDialog');
  if (!dialog) {
    dialog = document.createElement('dialog');
    dialog.id = 'DlVideoDialog';
    dialog.className = 'dl-video-dialog';
    dialog.innerHTML = `<button type="button" class="dl-video-dialog__close" aria-label="${strings().close || 'Close'}">✕</button><div class="dl-video-dialog__frame"></div>`;
    dialog.querySelector('button').addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => (dialog.querySelector('.dl-video-dialog__frame').innerHTML = ''));
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });
    document.body.append(dialog);
  }
  const frame = document.createElement('iframe');
  frame.src = src;
  frame.title = trigger.dataset.dlVideoTitle || 'Video';
  frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
  frame.allowFullscreen = true;
  dialog.querySelector('.dl-video-dialog__frame').replaceChildren(frame);
  dialog.showModal();
});

/* ---------- Product page: make it a pair ---------- */

/* Formats an amount in cents in the active currency, without trailing zeros unless withCents is set. */
function formatMoney(cents, withCents = false) {
  const amount = (Number(cents) || 0) / 100;
  const digits = withCents || !Number.isInteger(amount) ? 2 : 0;
  try {
    return new Intl.NumberFormat(root.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
      minimumFractionDigits: digits,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(digits);
  }
}

/* Awaits a Horizon product select event and returns its result ({ detail: { html, resource } }), or null. */
async function productSelectResult(event) {
  try {
    return (await event.promise) || null;
  } catch {
    return null;
  }
}

/* "Make it a pair": adds two instead of one. With Horizon's quantity selector in the buy box, the
   toggle sets it to 2 and stays in sync with it. With the quantity block hidden (the Dloria
   templates hide it), the toggle owns the choice and sets quantity 2 on the form as it submits. */
class DlPairToggle extends HTMLElement {
  async connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.button = this.querySelector('[data-dl-pair]');
    this.price = this.querySelector('[data-dl-pair-price]');
    // Keep cents in later prices when the store's money format showed them in the first one
    this.withCents = /\d[.,]\d{2}(?!\d)/.test(this.price?.textContent || '');
    if (!this.button || !window.customElements) return;

    if (this.scope().querySelector('quantity-selector-component')) {
      await Promise.race([
        customElements.whenDefined('quantity-selector-component'),
        new Promise((resolve) => setTimeout(resolve, 4000)),
      ]);
      if (signal.aborted || !this.isConnected) return;
    }
    const selector = this.selector();
    if (selector && this.maxQuantity(selector) < 2) return;

    this.hidden = false;
    this.button.addEventListener('click', () => this.toggle(), { signal });
    // Window capture runs before the theme's delegated submit handler on document
    window.addEventListener('submit', (event) => this.applyToForm(event), { capture: true, signal });
    document.addEventListener(
      'quantity-selector:update',
      (event) => {
        if (event.target === this.selector()) this.sync();
      },
      { signal }
    );
    this.closest('.shopify-section')?.addEventListener(
      'shopify:product:select',
      async (event) => {
        const result = await productSelectResult(event);
        if (signal.aborted) return;
        // Prefer the server-rendered price so it matches the store's money format
        const text = result?.detail?.html
          ?.querySelector('.product-details product-price [ref="priceContainer"] .price bdi')
          ?.textContent?.trim();
        const variant = result?.detail?.resource;
        if (this.price && text) this.price.textContent = text;
        else if (this.price && variant?.price != null) this.price.textContent = formatMoney(variant.price, this.withCents);
        this.sync();
      },
      { signal }
    );
    this.sync();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  scope() {
    return this.closest('.product-details') || this.closest('.shopify-section') || document;
  }

  selector() {
    const element = this.scope().querySelector('quantity-selector-component');
    return element && typeof element.setValue === 'function' ? element : null;
  }

  maxQuantity(selector) {
    const max = typeof selector.getEffectiveMax === 'function' ? selector.getEffectiveMax() : null;
    return max == null ? Infinity : Number(max);
  }

  toggle() {
    const selector = this.selector();
    const pressed = this.button.getAttribute('aria-pressed') === 'true';
    if (!selector) {
      this.button.setAttribute('aria-pressed', String(!pressed));
      return;
    }
    const min = parseInt(selector.refs?.quantityInput?.min, 10) || 1;
    const value = pressed ? min : Math.max(min, Math.min(2, this.maxQuantity(selector)));
    selector.setValue(String(value));
    selector.updateButtonStates?.();
    selector.onQuantityChange?.();
    this.sync();
  }

  /**
   * Without a quantity selector, sets quantity 2 (or back to the default) on this buy box's form
   * just before the theme reads it.
   * @param {SubmitEvent} event
   */
  applyToForm(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || this.selector()) return;
    const component = form.closest('product-form-component');
    if (!(component instanceof HTMLElement) || !this.scope().contains(component)) return;

    let input = form.querySelector('input[data-dl-pair-quantity]');
    if (this.button.getAttribute('aria-pressed') === 'true') {
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        input.dataset.dlPairQuantity = '';
        form.append(input);
      }
      input.value = '2';
      // Horizon falls back to data-quantity-default when it queues an add during a variant change
      component.dataset.dlPairDefault ??= component.dataset.quantityDefault || '1';
      component.dataset.quantityDefault = '2';
    } else {
      input?.remove();
      if (component.dataset.dlPairDefault) {
        component.dataset.quantityDefault = component.dataset.dlPairDefault;
        delete component.dataset.dlPairDefault;
      }
    }
  }

  sync() {
    // Without a quantity selector the toggle's own pressed state is the choice
    const selector = this.selector();
    if (!selector) return;
    const value = parseInt(selector.getValue?.() ?? '1', 10);
    this.button?.setAttribute('aria-pressed', String(value === 2));
  }
}

/* ---------- Product page: complete the set ---------- */

class DlSetPicker extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    try {
      this.text = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '{}');
    } catch {
      this.text = {};
    }
    this.inputs = [...this.querySelectorAll('input[data-dl-set-item]')];
    this.count = this.querySelector('[data-dl-set-count]');
    this.total = this.querySelector('[data-dl-set-total]');
    this.button = this.querySelector('[data-dl-set-add]');

    this.addEventListener(
      'change',
      (event) => {
        if (event.target instanceof HTMLInputElement && event.target.matches('[data-dl-set-item]')) this.render();
      },
      { signal }
    );
    this.button?.addEventListener('click', () => this.add(), { signal });
    this.querySelectorAll('[data-dl-set-choose]').forEach((button) =>
      button.addEventListener(
        'click',
        () => {
          button.setAttribute('aria-busy', 'true');
          openQuickAdd({
            url: button.dataset.url,
            title: button.dataset.title,
            productId: button.dataset.productId,
            optionsCount: Number(button.dataset.optionsCount) || 2,
            host: this,
          }).finally(() => button.removeAttribute('aria-busy'));
        },
        { signal }
      )
    );
    document.addEventListener('shopify:product:select', (event) => this.onProductSelect(event), { signal });
    this.render();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  picked() {
    return this.inputs.filter((input) => input.checked && !input.disabled);
  }

  render() {
    const picked = this.picked();
    const cents = picked.reduce((sum, input) => sum + (Number(input.dataset.price) || 0), 0);
    if (this.total) this.total.textContent = formatMoney(cents);
    if (this.count) this.count.textContent = this.text.counts?.[picked.length] ?? '';
    if (this.button) {
      this.button.disabled = picked.length === 0;
      this.button.textContent = picked.length ? this.text.labels?.[picked.length] ?? '' : this.text.empty ?? '';
    }
  }

  async onProductSelect(event) {
    const current = this.inputs.find((input) => input.hasAttribute('data-dl-set-current'));
    // The event carries a product GID, for example gid://shopify/Product/123
    const productId = String(event.product?.id ?? '').split('/').pop();
    if (!current || productId !== current.dataset.productId) return;
    const variant = (await productSelectResult(event))?.detail?.resource;
    if (!variant?.id || !this.isConnected) return;
    current.dataset.variantId = String(variant.id);
    current.dataset.price = String(variant.price);
    current.disabled = !variant.available;
    const price = current.closest('[data-dl-set-row]')?.querySelector('[data-dl-set-price]');
    if (price) price.textContent = formatMoney(variant.price);
    this.render();
  }

  async add() {
    const picked = this.picked();
    if (!picked.length || !this.button) return;
    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');
    try {
      await addLines(picked.map((input) => ({ id: input.dataset.variantId, quantity: 1 })));
      if (!cartDrawerAutoOpens()) {
        const names = picked.map((input) => input.dataset.title).filter(Boolean);
        Toast.show({ title: names.join(' + '), image: picked[0].dataset.image || '' });
      }
    } catch (error) {
      console.error(error);
      Toast.show({ title: error.message, image: '', error: true });
    } finally {
      this.button.removeAttribute('aria-busy');
      this.render();
    }
  }
}

/* ---------- Collection description: read more ---------- */

/* Collapses a long description to a short preview with a toggle. Without JavaScript, or when the
   text is short, the full description shows. */
class DlReadMore extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.body = this.querySelector('[data-dl-read-more-body]');
    this.toggleButton = this.querySelector('[data-dl-read-more-toggle]');
    if (!this.body || !this.toggleButton) return;

    this.setAttribute('data-collapsible', '');
    if (this.body.scrollHeight <= this.body.clientHeight + 8) {
      this.removeAttribute('data-collapsible');
      return;
    }
    this.toggleButton.hidden = false;
    this.toggleButton.addEventListener('click', () => this.toggle(), { signal: this.abort.signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  toggle() {
    const expanded = !this.hasAttribute('data-expanded');
    this.toggleAttribute('data-expanded', expanded);
    this.toggleButton.setAttribute('aria-expanded', String(expanded));
    this.toggleButton.textContent = expanded ? this.toggleButton.dataset.labelLess : this.toggleButton.dataset.labelMore;
    // Collapsing a long text can leave the reader below it; bring the heading back into view
    if (!expanded) this.scrollIntoView({ block: 'nearest', behavior: 'instant' });
  }
}

/* ---------- Product page: variant menu in the sticky add-to-cart bar ---------- */

/* Choosing a variant in the bar selects the same options in the main variant picker, which then
   updates the price, photos, address and the bar itself. Changes made in the main picker flow back. */
class DlStickyVariant extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.select = this.querySelector('select');
    if (!this.select) return;
    this.select.addEventListener('change', () => this.apply(), { signal });
    document.addEventListener('shopify:product:select', (event) => this.sync(event), { signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  picker() {
    const productId = this.select?.dataset.productId;
    return this.closest('.shopify-section')?.querySelector(`variant-picker[data-product-id="${productId}"]`) || null;
  }

  async apply() {
    const option = this.select.selectedOptions[0];
    let names = [];
    let values = [];
    try {
      names = JSON.parse(this.select.dataset.optionNames || '[]');
      values = JSON.parse(option?.dataset.options || '[]');
    } catch {
      return;
    }
    this.toggleAttribute('data-busy', true);
    try {
      // One option at a time: the picker re-renders after each change
      for (let index = 0; index < values.length; index += 1) {
        const picker = this.picker();
        if (!picker) return;
        const radio = [...picker.querySelectorAll('input[type="radio"]')].find(
          (input) => input.dataset.optionName === names[index] && input.value === values[index]
        );
        if (radio) {
          if (radio.checked) continue;
          radio.checked = true;
          const updated = this.nextUpdate();
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          await updated;
          continue;
        }
        const dropdown = [...picker.querySelectorAll('select')].find((select) =>
          [...select.options].some((item) => item.dataset.optionName === names[index] && item.value === values[index])
        );
        if (dropdown && dropdown.value !== values[index]) {
          dropdown.value = values[index];
          const updated = this.nextUpdate();
          dropdown.dispatchEvent(new Event('change', { bubbles: true }));
          await updated;
        }
      }
    } finally {
      this.removeAttribute('data-busy');
    }
  }

  /* Resolves once the variant picker has finished its update after a change. */
  nextUpdate() {
    return new Promise((resolve) => {
      const timer = setTimeout(resolve, 5000);
      document.addEventListener(
        'shopify:product:select',
        async (event) => {
          try {
            await event.promise;
          } catch {}
          clearTimeout(timer);
          resolve();
        },
        { once: true }
      );
    });
  }

  async sync(event) {
    if (String(event.product?.id ?? '').split('/').pop() !== this.select?.dataset.productId) return;
    let variant = null;
    try {
      variant = (await event.promise)?.detail?.resource;
    } catch {
      return;
    }
    if (!variant?.id) return;
    requestAnimationFrame(() => {
      const select = this.querySelector('select');
      if (select && select.value !== String(variant.id)) select.value = String(variant.id);
    });
  }
}

/* ---------- Product page: tall sticky details ---------- */

/* Horizon pins the product details column while the photos scroll. A column taller than the space
   under the header instead moves with the page until its top or bottom edge reaches the screen edge,
   then holds there, so every part stays reachable and opening an accordion never makes it jump. */
const stickyDetails = new Map();
let stickyFrame = 0;
let stickyScroll = 0;

function stickyLimits(details) {
  const header = document.querySelector('dl-header[data-sticky]');
  const maxTop = header ? Math.round(header.getBoundingClientRect().height) + 20 : 0;
  const minTop = Math.min(maxTop, window.innerHeight - details.offsetHeight - 24);
  return { minTop, maxTop };
}

function fitStickyDetails() {
  stickyFrame = 0;
  const now = scrollOffset();
  const delta = now - stickyScroll;
  stickyScroll = now;
  for (const [details, top] of stickyDetails) {
    if (!details.isConnected) {
      stickyDetails.delete(details);
      continue;
    }
    const { minTop, maxTop } = stickyLimits(details);
    const next = Math.round(Math.min(maxTop, Math.max(minTop, (top ?? maxTop) - delta)));
    if (next === top) continue;
    stickyDetails.set(details, next);
    details.style.setProperty('--sticky-header-offset', `${next}px`);
  }
}

function scheduleStickyFit() {
  if (!stickyFrame && stickyDetails.size) stickyFrame = requestAnimationFrame(fitStickyDetails);
}

const stickyObserver = 'ResizeObserver' in window ? new ResizeObserver(scheduleStickyFit) : null;

function initStickyDetails(scope = document) {
  scope.querySelectorAll('.product-information .product-details.sticky-content--desktop').forEach((details) => {
    if (stickyDetails.has(details)) return;
    stickyDetails.set(details, null);
    stickyObserver?.observe(details);
  });
  if (!stickyDetails.size) return;
  const header = document.querySelector('dl-header[data-sticky]');
  if (header) stickyObserver?.observe(header);
  stickyScroll = scrollOffset();
  scheduleStickyFit();
}

onAnyScroll(scheduleStickyFit);
window.addEventListener('resize', scheduleStickyFit);

/* ---------- Collection toolbar: stuck under the header ---------- */

/* While the collection toolbar is stuck under the header, the root carries data-dl-toolbar-stuck so
   dloria.css can give it the header's glass. The flag lives on the root because filtering re-renders
   the toolbar. */
const TOOLBAR = '.dl-collection :is(.facets-block-wrapper--horizontal, .facets-toggle)';
let toolbarFrame = 0;

function updateToolbarStuck() {
  toolbarFrame = 0;
  let stuck = false;
  if (scrollOffset() > 0) {
    for (const bar of document.querySelectorAll(TOOLBAR)) {
      if (!bar.offsetHeight) continue;
      const top = parseFloat(getComputedStyle(bar).top) || 0;
      if (bar.getBoundingClientRect().top <= top + 1) stuck = true;
    }
  }
  root.toggleAttribute('data-dl-toolbar-stuck', stuck);
}

function scheduleToolbarCheck() {
  if (!toolbarFrame) toolbarFrame = requestAnimationFrame(updateToolbarStuck);
}

onAnyScroll(scheduleToolbarCheck);
window.addEventListener('resize', scheduleToolbarCheck);

/* ---------- Boot ---------- */

const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};

define('dl-header', DlHeader);
define('dl-menu-drawer', DlMenuDrawer);
define('dl-rotator', DlRotator);
define('dl-product-card', DlProductCard);
define('dl-carousel', DlRail);
define('dl-hotspot', DlHotspot);
define('dl-engraver', DlEngraver);
define('dl-pair-toggle', DlPairToggle);
define('dl-set-picker', DlSetPicker);
define('dl-sticky-variant', DlStickyVariant);
define('dl-read-more', DlReadMore);

function boot(scope = document) {
  if (scope !== document || !window.DloriaRevealReady) initReveal(scope);
  initStickyDetails(scope);
  initParallax();
  syncWishButtons(scope);
  scheduleToolbarCheck();
}

initCartFocusRescue();
initCartScrim();

window.DloriaReady = true;
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
else boot();

document.addEventListener('shopify:section:load', (event) => boot(event.target));
document.addEventListener('shopify:section:unload', () => setTimeout(initParallax, 0));
